import type { App } from './App';
import type { ClassProfile, GameConfig, GameMode, RewardEntry, Student } from '../types';
import { getAvailableMaps } from '../core/maps';
import { getSettings, saveSettings } from '../storage/settings';
import { parseAndCreateStudents } from '../utils/parser';
import { shuffle, uuid } from '../utils/helpers';

const MODE_LABELS: Record<GameMode, string> = {
  single: '한 명 뽑기',
  multi: '여러 명 뽑기',
  team: '팀 나누기',
  reward: '보상 룰렛',
};

const MAX_PICK_COUNT = 99;
const MAX_TEAM_COUNT = 40;

export class RosterScreen {
  private el: HTMLElement;
  private app: App;
  private mode: GameMode;
  private profile: ClassProfile | null;
  private selected: Set<string> = new Set();
  private pickCount = 3;
  private teamCount = 2;
  private rewards: RewardEntry[] = [];
  private mapId: string;
  private courseLength: 'normal' | 'medium' | 'long' = 'normal';
  private studentOrder: string[] = [];
  private itemsEnabled = false;
  private shuffleParticipants = true;
  private students: Student[] = [];
  private winnerRule: 'first' | 'last' | 'custom' = 'first';
  private winnerRank = 1;
  private draggedRewardId: string | null = null;
  private randomSelectCount = 0;
  private useRandomFinalObstacles = true;

  constructor(container: HTMLElement, app: App, mode: GameMode) {
    this.app = app;
    this.mode = mode;
    this.profile = app.getActiveProfile();
    const saved = getSettings();
    this.mapId = saved.selectedMapId;
    const maps = getAvailableMaps();
    if (!maps.some((m) => m.id === this.mapId)) this.mapId = maps[0].id;
    this.shuffleParticipants = saved.shuffleParticipants;
    this.itemsEnabled = saved.itemsEnabled;
    this.useRandomFinalObstacles = saved.useRandomFinalObstacles;

    this.el = document.createElement('div');
    this.el.className = 'roster-screen';
    container.appendChild(this.el);

    // Auto-load if exactly one class saved
    const profiles = app.getProfiles();
    if (profiles.length === 1) {
      this.addStudents(profiles[0].students.filter((s) => !s.excluded));
    }

    this.render();
  }

  private render() {
    const { mode } = this;

    this.el.innerHTML = `
      <div class="roster-header">
        <button class="btn-back" id="btn-back">← 뒤로</button>
        <div class="roster-title-wrap">
          <span class="mode-tag">${MODE_LABELS[mode]}</span>
        </div>
      </div>

      <div class="roster-body">
        <main class="roster-primary">
          ${this.renderClassSection()}
          ${this.renderStudentSection()}
        </main>

        <aside class="roster-setup">
          ${this.renderModeOptions()}
          ${this.renderGameOptions()}
          ${this.renderMapSelector()}
        </aside>
      </div>

      <div class="roster-footer">
        <div class="roster-summary">
          <span id="summary-text">${this.getSummaryText()}</span>
        </div>
        <button class="btn-start ${this.canStart() ? '' : 'disabled'}" id="btn-start">
          🎲 시작하기
        </button>
      </div>
    `;

    this.bindEvents();
  }

  private renderClassSection(): string {
    const profiles = this.app.getProfiles();
    return `
      <div class="section-card roster-import-card">
        <div class="section-heading-row">
          <div class="section-title">명단</div>
          <button class="btn-sm btn-danger" id="btn-clear-roster">비우기</button>
        </div>
        ${profiles.length > 1 ? `
          <div class="class-btn-list">
            ${profiles.map((p) => `
              <button class="btn-class-load" data-load-class="${p.id}">
                <span class="class-btn-name">${p.name}</span>
                <span class="class-btn-count">${p.students.length}명</span>
              </button>
            `).join('')}
          </div>
        ` : profiles.length === 1 ? `
          <div class="class-single-hint">
            <span class="class-btn-name">${profiles[0].name}</span>
            <span class="class-btn-count">${profiles[0].students.length}명 자동 불러옴</span>
          </div>
        ` : ''}
        <details class="quick-add-panel" ${profiles.length === 0 || this.students.length === 0 ? 'open' : ''}>
          <summary>직접 입력</summary>
          <textarea class="quick-input" id="quick-input" rows="5"
            placeholder="이름을 입력하세요&#10;줄바꿈, 쉼표, 탭 구분자 모두 가능&#10;&#10;예) 김철수, 이영희&#10;박민수*2"></textarea>
          <div class="inline-actions">
            <button class="btn-primary-sm" id="btn-add-quick">명단 추가</button>
          </div>
        </details>
      </div>
    `;
  }

  private renderStudentSection(): string {
    return `
      <div class="section-card student-workspace">
        <div class="student-list-header">
          <span class="student-list-title">참가자</span>
          <span class="selected-count">${this.selected.size}명 선택</span>
        </div>
        <div class="student-toolbar">
          <button class="btn-sm" id="btn-select-all">전체선택</button>
          <button class="btn-sm" id="btn-deselect-all">전체해제</button>
          <div class="random-pick-wrap">
            <button class="btn-sm" id="btn-random">랜덤 선택</button>
            <div class="inline-counter">
              <button class="btn-num" id="btn-rc-dec" type="button">−</button>
              <input class="num-display random-count-field" id="random-count" type="number" min="0" inputmode="numeric" value="${this.randomSelectCount}" aria-label="랜덤 선택 인원" />
              <button class="btn-num" id="btn-rc-inc" type="button">+</button>
              <span class="num-label">${this.randomSelectCount === 0 ? '자동' : '명'}</span>
            </div>
          </div>
          <button class="btn-sm" id="btn-shuffle">섞기</button>
        </div>
        <div class="student-grid" id="student-grid">
          ${this.renderStudentGrid()}
        </div>
      </div>
    `;
  }

  private renderModeOptions(): string {
    if (this.mode === 'multi') {
      return `
        <div class="section-card">
          <div class="section-title">인원 수</div>
          <div class="number-input">
            <button class="btn-num" id="btn-pick-dec" type="button" aria-label="뽑을 인원 줄이기">−</button>
            <input class="num-display num-field" id="pick-count" type="number" min="1" max="${MAX_PICK_COUNT}" inputmode="numeric" value="${this.pickCount}" aria-label="뽑을 인원" />
            <button class="btn-num" id="btn-pick-inc" type="button" aria-label="뽑을 인원 늘리기">+</button>
            <span class="num-label">명</span>
          </div>
        </div>
      `;
    }

    if (this.mode === 'team') {
      return `
        <div class="section-card">
          <div class="section-title">팀 수</div>
          <div class="number-input">
            <button class="btn-num" id="btn-team-dec" type="button" aria-label="팀 수 줄이기">−</button>
            <input class="num-display num-field" id="team-count" type="number" min="2" max="${MAX_TEAM_COUNT}" inputmode="numeric" value="${this.teamCount}" aria-label="팀 수" />
            <button class="btn-num" id="btn-team-inc" type="button" aria-label="팀 수 늘리기">+</button>
            <span class="num-label">팀</span>
          </div>
        </div>
      `;
    }

    if (this.mode === 'reward') {
      return `
        <div class="section-card">
          <div class="section-title">보상 목록</div>
          <p class="section-help">등록한 보상 수만큼 당첨자를 뽑고, 위에서부터 1등 순서로 나눠줘요.</p>
          <textarea class="quick-input" id="reward-bulk" rows="4"
            placeholder="보상을 여러 줄로 입력 후 일괄 추가&#10;예) 칭찬스티커&#10;발표 면제권"></textarea>
          <button class="btn-sm" id="btn-add-reward-bulk">보상 일괄 추가</button>
          <div class="reward-list" id="reward-list">
            ${this.rewards.map((r, i) => `
              <div class="reward-item" draggable="true" data-id="${r.id}">
                <span class="reward-order">${i + 1}</span>
                <span class="reward-drag" title="드래그해서 순서 변경">⋮⋮</span>
                <input type="text" class="reward-input" data-id="${r.id}" value="${this.escapeAttr(r.name)}" placeholder="보상명" />
                <div class="reward-count-control">
                  <button class="btn-num btn-reward-dec" data-id="${r.id}" type="button" aria-label="보상 수량 줄이기">−</button>
                  <input class="reward-count-input" data-id="${r.id}" type="number" min="1" max="99" inputmode="numeric" value="${r.count}" aria-label="보상 수량" />
                  <button class="btn-num btn-reward-inc" data-id="${r.id}" type="button" aria-label="보상 수량 늘리기">+</button>
                </div>
                <button class="btn-remove-reward" data-id="${r.id}" type="button">×</button>
              </div>
            `).join('')}
            ${this.rewards.length === 0 ? '<div class="reward-empty">보상을 추가해 주세요</div>' : ''}
          </div>
          <button class="btn-primary-sm" id="btn-add-reward">+ 보상 추가</button>
        </div>
      `;
    }

    return '';
  }

  private renderGameOptions(): string {
    return `
      <div class="section-card">
        <div class="section-title">게임 설정</div>
        ${this.mode === 'single' ? `
          <label class="form-label">당첨 순서</label>
          <div class="winner-rule-row">
            <select class="select-class" id="winner-rule">
              <option value="first" ${this.winnerRule === 'first' ? 'selected' : ''}>첫 번째 도착</option>
              <option value="last" ${this.winnerRule === 'last' ? 'selected' : ''}>마지막 도착</option>
              <option value="custom" ${this.winnerRule === 'custom' ? 'selected' : ''}>특정 순서</option>
            </select>
            <input class="form-input winner-rank-input" id="winner-rank" type="number" min="1" value="${this.winnerRank}" ${this.winnerRule === 'custom' ? '' : 'disabled'} />
          </div>
        ` : ''}
        <div class="compact-option-grid">
          <label class="toggle-switch">
            <input type="checkbox" id="opt-shuffle" ${this.shuffleParticipants ? 'checked' : ''} />
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
            <strong>참가자 섞기</strong>
          </label>
          <label class="toggle-switch">
            <input type="checkbox" id="opt-items" ${this.itemsEnabled ? 'checked' : ''} />
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
            <strong>아이템 모드</strong>
          </label>
          ${!this.isCustomMap() ? `
          <label class="toggle-switch" style="grid-column: 1 / -1;">
            <input type="checkbox" id="opt-random-obstacles" ${this.useRandomFinalObstacles ? 'checked' : ''} />
            <span class="toggle-track"><span class="toggle-thumb"></span></span>
            <strong>결승 랜덤 장애물</strong>
          </label>
          ` : ''}
        </div>
        <label class="form-label">맵 길이</label>
        <div class="pill-toggle-row${this.isCustomMap() ? ' disabled' : ''}" id="course-length-pills">
          <button class="pill-toggle ${this.courseLength === 'normal' ? 'active' : ''}" data-value="normal" ${this.isCustomMap() ? 'disabled' : ''}>보통</button>
          <button class="pill-toggle ${this.courseLength === 'medium' ? 'active' : ''}" data-value="medium" ${this.isCustomMap() ? 'disabled' : ''}>× 1.5</button>
          <button class="pill-toggle ${this.courseLength === 'long' ? 'active' : ''}" data-value="long" ${this.isCustomMap() ? 'disabled' : ''}>× 2</button>
        </div>
        ${this.isCustomMap() ? '<p class="map-notice">커스텀 맵에서는 맵 길이를 조정할 수 없어요.</p>' : ''}
      </div>
    `;
  }

  private renderMapSelector(): string {
    const maps = getAvailableMaps();
    return `
      <div class="section-card">
        <div class="section-title">맵</div>
        <div class="map-grid">
          ${maps.map((m) => `
            <button class="map-card ${m.id === this.mapId ? 'active' : ''}" data-map="${m.id}">
              <span class="map-icon">${m.thumbnail}</span>
              <span class="map-name">${m.name}</span>
              <span class="map-tags">${this.getMapTags(m.id).map((tag) => `<em>${tag}</em>`).join('')}</span>
              <span class="map-diff">${'★'.repeat(m.difficulty)}${'☆'.repeat(5 - m.difficulty)}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  private isCustomMap(): boolean {
    return this.mapId.startsWith('custom-');
  }

  private getRandomCount(): number {
    const all = this.getOrderedStudents();
    return this.randomSelectCount > 0
      ? Math.min(this.randomSelectCount, all.length)
      : Math.max(1, Math.ceil(all.length / 2));
  }

  private getMapTags(id: string): string[] {
    const tags: Record<string, string[]> = {
      'pinball-tower': ['핀볼', '튕김'],
      'canyon-race': ['협곡', '속도'],
      'reverse-gauntlet': ['역전', '혼돈'],
      'bubble-castle': ['버블', '변수'],
      'slalom-sprint': ['지그재그', '추격'],
    };
    return tags[id] ?? ['커스텀'];
  }

  private renderStudentGrid(): string {
    const students = this.students;
    if (students.length === 0) {
      return '<div class="roster-empty">명단을 등록해 주세요</div>';
    }
    const byId = new Map(students.map((s) => [s.id, s]));
    const ordered = this.studentOrder
      .map((id) => byId.get(id))
      .filter((s): s is Student => Boolean(s));
    return ordered.map((s) => `
      <button class="student-chip ${this.selected.has(s.id) ? 'selected' : ''} ${s.excluded ? 'excluded' : ''}"
              data-id="${s.id}" title="${s.name}">
        ${s.emoji ? `<span>${s.emoji}</span>` : ''}
        <span class="chip-name">${s.name}</span>
        ${s.pickCount > 0 ? `<span class="chip-count">${s.pickCount}</span>` : ''}
      </button>
    `).join('');
  }

  private getSummaryText(): string {
    const n = this.selected.size;
    if (this.mode === 'single') return `${n}명 중 1명을 뽑아요`;
    if (this.mode === 'multi') return `${n}명 중 ${this.pickCount}명을 뽑아요`;
    if (this.mode === 'team') return `${n}명을 ${this.teamCount}팀으로 나눕니다`;
    if (this.mode === 'reward') return `${n}명 중 ${this.rewardSlotCount()}명에게 보상을 줍니다`;
    return '';
  }

  private canStart(): boolean {
    if (this.selected.size < 1) return false;
    if (this.mode === 'multi' && this.selected.size < this.pickCount) return false;
    if (this.mode === 'team' && this.selected.size < this.teamCount) return false;
    if (this.mode === 'reward') {
      const rewardCount = this.rewardSlotCount();
      if (rewardCount < 1) return false;
      if (this.selected.size < rewardCount) return false;
    }
    return true;
  }

  private bindEvents() {
    this.el.querySelector('#btn-back')?.addEventListener('click', () => this.app.navigate('home'));

    // Class buttons
    this.el.querySelectorAll<HTMLButtonElement>('[data-load-class]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const profile = this.app.getProfiles().find((p) => p.id === btn.dataset.loadClass);
        if (!profile) return;
        this.addStudents(profile.students.filter((s) => !s.excluded));
      });
    });

    // Quick input
    this.el.querySelector('#btn-add-quick')?.addEventListener('click', () => {
      const ta = this.el.querySelector<HTMLTextAreaElement>('#quick-input');
      if (!ta || !ta.value.trim()) return;
      const students = parseAndCreateStudents(ta.value);
      this.addStudents(students);
      ta.value = '';
    });

    this.el.querySelector('#btn-clear-roster')?.addEventListener('click', () => {
      this.students = [];
      this.selected.clear();
      this.studentOrder = [];
      this.refreshGrid();
    });

    // Student selection
    this.el.querySelector('#student-grid')?.addEventListener('click', (e) => {
      const chip = (e.target as HTMLElement).closest<HTMLElement>('.student-chip');
      if (!chip || !chip.dataset.id) return;
      const id = chip.dataset.id;
      if (this.selected.has(id)) {
        this.selected.delete(id);
        chip.classList.remove('selected');
      } else {
        this.selected.add(id);
        chip.classList.add('selected');
      }
      this.updateSummary();
    });

    this.el.querySelector('#btn-select-all')?.addEventListener('click', () => {
      this.students.forEach((s) => this.selected.add(s.id));
      this.refreshGrid();
    });

    this.el.querySelector('#btn-deselect-all')?.addEventListener('click', () => {
      this.selected.clear();
      this.refreshGrid();
    });

    this.el.querySelector('#btn-random')?.addEventListener('click', () => {
      const all = this.getOrderedStudents();
      const n = this.getRandomCount();
      const shuffled = shuffle(all).slice(0, n);
      this.selected.clear();
      shuffled.forEach((s) => this.selected.add(s.id));
      this.refreshGrid();
    });

    const updateRandomCountDisplay = () => {
      const inp = this.el.querySelector<HTMLInputElement>('#random-count');
      const label = this.el.querySelector<HTMLElement>('.num-label');
      if (inp) inp.value = String(this.randomSelectCount);
      if (label) label.textContent = this.randomSelectCount === 0 ? '자동' : '명';
    };
    this.el.querySelector('#btn-rc-dec')?.addEventListener('click', () => {
      this.randomSelectCount = Math.max(0, this.randomSelectCount - 1);
      updateRandomCountDisplay();
    });
    this.el.querySelector('#btn-rc-inc')?.addEventListener('click', () => {
      this.randomSelectCount = this.randomSelectCount + 1;
      updateRandomCountDisplay();
    });
    this.el.querySelector('#random-count')?.addEventListener('input', (e) => {
      const v = parseInt((e.target as HTMLInputElement).value, 10);
      this.randomSelectCount = isNaN(v) || v < 0 ? 0 : v;
      updateRandomCountDisplay();
    });

    this.el.querySelector('#btn-shuffle')?.addEventListener('click', () => {
      this.studentOrder = shuffle(this.studentOrder);
      this.refreshGrid();
    });

    this.el.querySelector('#opt-shuffle')?.addEventListener('change', (e) => {
      this.shuffleParticipants = (e.target as HTMLInputElement).checked;
      saveSettings({ shuffleParticipants: this.shuffleParticipants });
    });

    this.el.querySelector('#opt-items')?.addEventListener('change', (e) => {
      this.itemsEnabled = (e.target as HTMLInputElement).checked;
      saveSettings({ itemsEnabled: this.itemsEnabled });
    });
    this.el.querySelector('#opt-random-obstacles')?.addEventListener('change', (e) => {
      this.useRandomFinalObstacles = (e.target as HTMLInputElement).checked;
      saveSettings({ useRandomFinalObstacles: this.useRandomFinalObstacles });
    });
    this.el.querySelector('#course-length-pills')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.pill-toggle');
      if (!btn?.dataset.value) return;
      this.courseLength = btn.dataset.value as 'normal' | 'medium' | 'long';
      this.el.querySelectorAll('#course-length-pills .pill-toggle').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });

    this.el.querySelector('#winner-rule')?.addEventListener('change', (e) => {
      this.winnerRule = (e.target as HTMLSelectElement).value as typeof this.winnerRule;
      this.render();
    });

    this.el.querySelector('#winner-rank')?.addEventListener('input', (e) => {
      this.winnerRank = Math.max(1, parseInt((e.target as HTMLInputElement).value, 10) || 1);
      this.updateSummary();
    });

    // Pick count
    this.el.querySelector('#btn-pick-dec')?.addEventListener('click', () => {
      this.pickCount = this.clampCount(this.pickCount - 1, 1, MAX_PICK_COUNT);
      this.updatePickCount();
    });
    this.el.querySelector('#btn-pick-inc')?.addEventListener('click', () => {
      this.pickCount = this.clampCount(this.pickCount + 1, 1, MAX_PICK_COUNT);
      this.updatePickCount();
    });
    this.el.querySelector('#pick-count')?.addEventListener('input', (e) => {
      this.pickCount = this.clampCount((e.target as HTMLInputElement).value, 1, MAX_PICK_COUNT);
      this.updatePickCount();
    });

    // Team count
    this.el.querySelector('#btn-team-dec')?.addEventListener('click', () => {
      this.teamCount = this.clampCount(this.teamCount - 1, 2, MAX_TEAM_COUNT);
      this.updateTeamCount();
    });
    this.el.querySelector('#btn-team-inc')?.addEventListener('click', () => {
      this.teamCount = this.clampCount(this.teamCount + 1, 2, MAX_TEAM_COUNT);
      this.updateTeamCount();
    });
    this.el.querySelector('#team-count')?.addEventListener('input', (e) => {
      this.teamCount = this.clampCount((e.target as HTMLInputElement).value, 2, MAX_TEAM_COUNT);
      this.updateTeamCount();
    });

    // Map selection
    this.el.querySelectorAll<HTMLButtonElement>('.map-card').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wasCustom = this.isCustomMap();
        this.mapId = btn.dataset.map!;
        saveSettings({ selectedMapId: this.mapId });
        const isNowCustom = this.isCustomMap();
        if (wasCustom !== isNowCustom) {
          if (isNowCustom) {
            this.courseLength = 'normal';
            this.useRandomFinalObstacles = false;
          } else {
            this.useRandomFinalObstacles = true;
          }
          this.render();
        } else {
          this.el.querySelectorAll('.map-card').forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
        }
      });
    });

    // Reward inputs
    this.el.querySelectorAll<HTMLInputElement>('.reward-input').forEach((inp) => {
      inp.addEventListener('input', () => {
        const reward = this.findReward(inp.dataset.id);
        if (reward) reward.name = inp.value;
        this.updateSummary();
      });
    });
    this.el.querySelector('#btn-add-reward')?.addEventListener('click', () => {
      this.rewards.push(this.createReward('새 보상', 1));
      this.render();
    });

    this.el.querySelector('#btn-add-reward-bulk')?.addEventListener('click', () => {
      const text = this.el.querySelector<HTMLTextAreaElement>('#reward-bulk')?.value ?? '';
      const items = text.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean);
      if (items.length === 0) return;
      this.rewards.push(...items.map((name) => this.createReward(name, 1)));
      this.render();
    });

    this.el.querySelectorAll<HTMLButtonElement>('.btn-remove-reward').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.rewards = this.rewards.filter((r) => r.id !== btn.dataset.id);
        this.render();
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.btn-reward-dec').forEach((btn) => {
      btn.addEventListener('click', () => this.changeRewardCount(btn.dataset.id, -1));
    });
    this.el.querySelectorAll<HTMLButtonElement>('.btn-reward-inc').forEach((btn) => {
      btn.addEventListener('click', () => this.changeRewardCount(btn.dataset.id, 1));
    });
    this.el.querySelectorAll<HTMLInputElement>('.reward-count-input').forEach((inp) => {
      inp.addEventListener('input', () => {
        const reward = this.findReward(inp.dataset.id);
        if (!reward) return;
        reward.count = this.clampCount(inp.value, 1, 99);
        this.updateSummary();
      });
    });
    this.el.querySelectorAll<HTMLElement>('.reward-item').forEach((item) => {
      item.addEventListener('dragstart', () => {
        this.draggedRewardId = item.dataset.id ?? null;
        item.classList.add('dragging');
      });
      item.addEventListener('dragend', () => {
        this.draggedRewardId = null;
        item.classList.remove('dragging');
      });
      item.addEventListener('dragover', (e) => {
        e.preventDefault();
      });
      item.addEventListener('drop', (e) => {
        e.preventDefault();
        const rect = item.getBoundingClientRect();
        this.moveReward(item.dataset.id ?? null, e.clientY > rect.top + rect.height / 2);
      });
    });

    // Start button
    this.el.querySelector('#btn-start')?.addEventListener('click', () => {
      if (!this.canStart()) return;
      let students = this.getOrderedStudents().filter((s) => this.selected.has(s.id));
      if (this.shuffleParticipants) students = shuffle(students);
      const config: GameConfig = {
        mode: this.mode,
        students,
        pickCount: this.pickCount,
        teamCount: this.teamCount,
        teamMode: 'arrival',
        rewards: this.normalizedRewards(),
        mapId: this.mapId,
        courseLength: this.isCustomMap() ? 'normal' : this.courseLength,
        shuffleParticipants: this.shuffleParticipants,
        itemsEnabled: this.itemsEnabled,
        winnerRule: this.winnerRule,
        winnerRank: this.winnerRank,
        useRandomFinalObstacles: this.isCustomMap() ? false : this.useRandomFinalObstacles,
      };
      this.app.navigate('game', { config });
    });
  }

  private refreshGrid() {
    const grid = this.el.querySelector('#student-grid');
    if (grid) grid.innerHTML = this.renderStudentGrid();
    this.updateSummary();
  }

  private getOrderedStudents(): Student[] {
    const students = this.students;
    const byId = new Map(students.map((s) => [s.id, s]));
    const ordered = this.studentOrder
      .map((id) => byId.get(id))
      .filter((s): s is Student => Boolean(s));
    if (ordered.length !== students.length) {
      const seen = new Set(ordered.map((s) => s.id));
      ordered.push(...students.filter((s) => !seen.has(s.id)));
    }
    return ordered;
  }

  private updateSummary() {
    const el = this.el.querySelector('#summary-text');
    if (el) el.textContent = this.getSummaryText();
    const btn = this.el.querySelector('#btn-start');
    if (btn) btn.classList.toggle('disabled', !this.canStart());
    const cnt = this.el.querySelector('.selected-count');
    if (cnt) cnt.textContent = `${this.selected.size}명 선택`;
  }

  private addStudents(students: Student[]) {
    const seen = new Set(this.students.map((s) => s.name.trim()));
    const incoming = students.filter((s) => !seen.has(s.name.trim()));
    this.students.push(...incoming);
    incoming.forEach((s) => {
      this.selected.add(s.id);
      this.studentOrder.push(s.id);
    });
    this.refreshGrid();
  }

  private createReward(name: string, count: number): RewardEntry {
    return {
      id: uuid(),
      name,
      count: this.clampCount(count, 1, 99),
    };
  }

  private rewardSlotCount(): number {
    return this.rewards
      .filter((reward) => reward.name.trim())
      .reduce((sum, reward) => sum + Math.max(1, Math.floor(reward.count || 1)), 0);
  }

  private findReward(id?: string): RewardEntry | undefined {
    if (!id) return undefined;
    return this.rewards.find((reward) => reward.id === id);
  }

  private changeRewardCount(id: string | undefined, delta: number) {
    const reward = this.findReward(id);
    if (!reward) return;
    reward.count = this.clampCount(reward.count + delta, 1, 99);
    const input = this.el.querySelector<HTMLInputElement>(`.reward-count-input[data-id="${reward.id}"]`);
    if (input) input.value = String(reward.count);
    this.updateSummary();
  }

  private moveReward(targetId: string | null, afterTarget: boolean) {
    if (!this.draggedRewardId || !targetId || this.draggedRewardId === targetId) return;
    const from = this.rewards.findIndex((reward) => reward.id === this.draggedRewardId);
    const to = this.rewards.findIndex((reward) => reward.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = this.rewards.splice(from, 1);
    const targetIndex = this.rewards.findIndex((reward) => reward.id === targetId);
    this.rewards.splice(targetIndex + (afterTarget ? 1 : 0), 0, moved);
    this.render();
  }

  private normalizedRewards(): RewardEntry[] {
    return this.rewards
      .map((reward) => ({
        ...reward,
        name: reward.name.trim(),
        count: this.clampCount(reward.count, 1, 99),
      }))
      .filter((reward) => reward.name);
  }

  private escapeAttr(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private updatePickCount() {
    const el = this.el.querySelector<HTMLInputElement>('#pick-count');
    if (el && el.value !== String(this.pickCount)) el.value = String(this.pickCount);
    this.updateSummary();
  }

  private updateTeamCount() {
    const el = this.el.querySelector<HTMLInputElement>('#team-count');
    if (el && el.value !== String(this.teamCount)) el.value = String(this.teamCount);
    this.updateSummary();
  }

  private clampCount(value: string | number, min: number, max: number): number {
    const parsed = typeof value === 'number' ? value : parseInt(value, 10);
    if (!Number.isFinite(parsed)) return min;
    return Math.min(max, Math.max(min, parsed));
  }

  destroy() {
    this.el.remove();
  }
}
